import React, { useRef, useState } from 'react';
import {
  Avatar,
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownList,
  FormGroup,
  MenuToggle,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Progress,
  ProgressVariant,
  Select,
  SelectList,
  SelectOption,
  TextInput,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import { UserIcon } from '@patternfly/react-icons';
import { useAuth } from '../../context/AuthContext';
import { useBox } from '../../context/BoxContext';
import * as api from '../../utils/apiCalls';
import type { AsafBoxUserUpdate } from '../../../types/models';
import { Whitelabel } from '../../../types/models';

const WHITELABEL_OPTIONS: { value: Whitelabel; label: string }[] = [
  { value: Whitelabel.NONE, label: 'None' },
  { value: Whitelabel.HYPR_TRAINING, label: 'HYPR Training' },
  { value: Whitelabel.LOCKERROOMTLV, label: 'Locker Room TLV' },
];

const AppToolbar: React.FC = () => {
  const { user, logout, refreshProfile } = useAuth();
  const { boxes, selectedBoxId, selectBox, boxLocations, selectedBoxLocationId, selectBoxLocation } = useBox();

  const [isBoxOpen, setIsBoxOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshMessage, setRefreshMessage] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const [showWhitelabelModal, setShowWhitelabelModal] = useState(false);
  const [editWhitelabels, setEditWhitelabels] = useState<Whitelabel[]>([]);
  const [whitelabelSaving, setWhitelabelSaving] = useState(false);
  const [whitelabelError, setWhitelabelError] = useState('');

  const selectedLabel = boxes.find((b) => b.id === selectedBoxId)?.name || 'Select a box';
  const selectedLocationLabel = boxLocations.find((l) => l.id === selectedBoxLocationId)?.name || 'Select a location';

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!newPassword) {
      setPasswordError('Password cannot be empty');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const body: AsafBoxUserUpdate = { password: newPassword };
      await api.put(`/users/${user!.userId}`, body);
      closePasswordModal();
    } catch (err: unknown) {
      setPasswordError((err as { message?: string }).message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshProgress(0);
    setRefreshMessage('Starting refresh…');
    setRefreshError('');
    setIsProfileOpen(false);

    try {
      const { taskId } = await api.post<{ taskId: string }>('/users/refresh');
      const controller = new AbortController();
      abortRef.current = controller;

      await api.streamSSE(
        `/users/refresh/events?taskId=${taskId}`,
        (event) => {
          setRefreshProgress(Math.round(event.progress * 100));
          setRefreshMessage(event.message);
          if (event.error) {
            setRefreshError(event.error);
          }
        },
        controller.signal,
      );

      await refreshProfile();
    } catch (err: unknown) {
      if ((err as Error).name !== 'AbortError') {
        setRefreshError((err as { message?: string }).message || 'Refresh failed');
      }
    } finally {
      abortRef.current = null;
      setRefreshing(false);
    }
  };

  const closeRefreshModal = () => {
    abortRef.current?.abort();
    setRefreshing(false);
    setRefreshError('');
  };

  const openWhitelabelModal = () => {
    setEditWhitelabels(user?.whitelabels ?? []);
    setWhitelabelError('');
    setShowWhitelabelModal(true);
  };

  const closeWhitelabelModal = () => {
    setShowWhitelabelModal(false);
    setWhitelabelError('');
  };

  const toggleWhitelabel = (wl: Whitelabel, checked: boolean) => {
    setEditWhitelabels((prev) =>
      checked ? [...prev, wl] : prev.filter((w) => w !== wl),
    );
  };

  const handleSaveWhitelabels = async () => {
    setWhitelabelError('');
    setWhitelabelSaving(true);
    try {
      const body: AsafBoxUserUpdate = { whitelabels: editWhitelabels };
      await api.put(`/users/${user!.userId}`, body);
      await refreshProfile();
      closeWhitelabelModal();
    } catch (err: unknown) {
      setWhitelabelError((err as { message?: string }).message || 'Failed to update whitelabels');
    } finally {
      setWhitelabelSaving(false);
    }
  };

  const profileDropdown = (
    <Dropdown
      isOpen={isProfileOpen}
      onOpenChange={setIsProfileOpen}
      onSelect={() => setIsProfileOpen(false)}
      popperProps={{ position: 'end' }}
      toggle={(toggleRef) => (
        <MenuToggle
          ref={toggleRef}
          onClick={() => setIsProfileOpen((prev) => !prev)}
          isExpanded={isProfileOpen}
          variant="plain"
        >
          {user?.profileImage ? (
            <Avatar src={user.profileImage} alt={user.username} />
          ) : (
            <UserIcon />
          )}
        </MenuToggle>
      )}
    >
      <DropdownList>
        <DropdownItem
          key="change-password"
          onClick={() => setShowPasswordModal(true)}
        >
          Change password
        </DropdownItem>
        <DropdownItem
          key="whitelabels"
          onClick={openWhitelabelModal}
        >
          Whitelabels
        </DropdownItem>
        <DropdownItem
          key="refresh"
          onClick={handleRefresh}
          isDisabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </DropdownItem>
        <DropdownItem key="logout" onClick={logout}>
          Log out
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );

  return (
    <>
      <Toolbar isFullHeight>
        <ToolbarContent>
          {boxes.length > 0 && (
            <ToolbarGroup align={{ default: 'alignStart' }}>
              <ToolbarItem>
                <Select
                  isOpen={isBoxOpen}
                  onOpenChange={setIsBoxOpen}
                  onSelect={(_e, value) => {
                    selectBox(value as string);
                    setIsBoxOpen(false);
                  }}
                  selected={selectedBoxId ?? undefined}
                  toggle={(toggleRef) => (
                    <MenuToggle
                      ref={toggleRef}
                      onClick={() => setIsBoxOpen((prev) => !prev)}
                      isExpanded={isBoxOpen}
                      className="asafbox-box-selector"
                    >
                      {selectedLabel}
                    </MenuToggle>
                  )}
                >
                  <SelectList>
                    {boxes.map((box) => (
                      <SelectOption key={box.id} value={box.id}>
                        {box.name || box.id}
                      </SelectOption>
                    ))}
                  </SelectList>
                </Select>
              </ToolbarItem>
              {boxLocations.length > 1 && (
                <ToolbarItem>
                  <Select
                    isOpen={isLocationOpen}
                    onOpenChange={setIsLocationOpen}
                    onSelect={(_e, value) => {
                      selectBoxLocation(value as string);
                      setIsLocationOpen(false);
                    }}
                    selected={selectedBoxLocationId ?? undefined}
                    toggle={(toggleRef) => (
                      <MenuToggle
                        ref={toggleRef}
                        onClick={() => setIsLocationOpen((prev) => !prev)}
                        isExpanded={isLocationOpen}
                        className="asafbox-box-selector"
                      >
                        {selectedLocationLabel}
                      </MenuToggle>
                    )}
                  >
                    <SelectList>
                      {boxLocations.map((loc) => (
                        <SelectOption key={loc.id} value={loc.id}>
                          {loc.name || loc.id}
                        </SelectOption>
                      ))}
                    </SelectList>
                  </Select>
                </ToolbarItem>
              )}
            </ToolbarGroup>
          )}
          <ToolbarItem align={{ default: 'alignEnd' }}>
            {profileDropdown}
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {showPasswordModal && (
        <Modal isOpen onClose={closePasswordModal} variant="small">
          <ModalHeader title="Change password" />
          <ModalBody>
            <FormGroup label="New password" fieldId="new-password">
              <TextInput
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(_e, val) => setNewPassword(val)}
              />
            </FormGroup>
            <FormGroup label="Confirm password" fieldId="confirm-password" style={{ marginTop: '1rem' }}>
              <TextInput
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(_e, val) => setConfirmPassword(val)}
              />
            </FormGroup>
            {passwordError && (
              <div style={{ color: 'var(--pf-t--global--color--status--danger--default)', marginTop: '0.5rem' }}>
                {passwordError}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={handleChangePassword} isLoading={saving} isDisabled={saving}>
              Save
            </Button>
            <Button variant="link" onClick={closePasswordModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {refreshing && (
        <Modal isOpen onClose={closeRefreshModal} variant="small">
          <ModalHeader title="Refreshing data" />
          <ModalBody>
            <Progress
              value={refreshProgress}
              title={refreshMessage}
              variant={refreshError ? ProgressVariant.danger : undefined}
            />
            {refreshError && (
              <div style={{ color: 'var(--pf-t--global--color--status--danger--default)', marginTop: '0.5rem' }}>
                {refreshError}
              </div>
            )}
          </ModalBody>
        </Modal>
      )}

      {showWhitelabelModal && (
        <Modal isOpen onClose={closeWhitelabelModal} variant="small">
          <ModalHeader title="Whitelabels" />
          <ModalBody>
            <FormGroup fieldId="edit-whitelabels">
              {WHITELABEL_OPTIONS.map((opt) => (
                <Checkbox
                  key={opt.value}
                  id={`edit-wl-${opt.value}`}
                  label={opt.label}
                  isChecked={editWhitelabels.includes(opt.value)}
                  onChange={(_e, checked) => toggleWhitelabel(opt.value, checked)}
                />
              ))}
            </FormGroup>
            {whitelabelError && (
              <div style={{ color: 'var(--pf-t--global--color--status--danger--default)', marginTop: '0.5rem' }}>
                {whitelabelError}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={handleSaveWhitelabels} isLoading={whitelabelSaving} isDisabled={whitelabelSaving}>
              Save
            </Button>
            <Button variant="link" onClick={closeWhitelabelModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default AppToolbar;
